import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { ArrowLeft, RefreshCw, Package, Bell, CheckCircle, Send, Phone, User, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

const StockNotifications = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isSalesman = currentUser?.role === 'salesman';
  const isManager = ['admin', 'owner', 'crm'].includes(currentUser?.role);

  const [parkedLeads, setParkedLeads] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [notifyMessage, setNotifyMessage] = useState('Stock has arrived! Please follow up with the client.');
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [tab, setTab] = useState(isSalesman ? 'my-notifications' : 'waiting-stock');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [];
      if (isManager) {
        promises.push(api.get('/crm/parked-stock-leads'));
      }
      promises.push(api.get('/crm/stock-notifications'));
      const results = await Promise.all(promises);
      if (isManager) {
        setParkedLeads(results[0].data);
        setNotifications(results[1].data);
      } else {
        setNotifications(results[0].data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [isManager]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleLeadSelect = (leadId) => {
    setSelectedLeadIds(prev =>
      prev.includes(leadId) ? prev.filter(id => id !== leadId) : [...prev, leadId]
    );
  };

  const selectAllInGroup = (groupLeads) => {
    const groupIds = groupLeads.map(l => l._id);
    const allSelected = groupIds.every(id => selectedLeadIds.includes(id));
    if (allSelected) {
      setSelectedLeadIds(prev => prev.filter(id => !groupIds.includes(id)));
    } else {
      setSelectedLeadIds(prev => [...new Set([...prev, ...groupIds])]);
    }
  };

  const handleNotify = async () => {
    if (selectedLeadIds.length === 0) return;
    setSending(true);
    try {
      await api.post('/crm/notify-stock-arrived', {
        lead_ids: selectedLeadIds,
        message: notifyMessage,
      });
      setSelectedLeadIds([]);
      setShowNotifyDialog(false);
      setNotifyMessage('Stock has arrived! Please follow up with the client.');
      fetchData();
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const markRead = async (notifId) => {
    try {
      await api.patch(`/crm/stock-notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n._id === notifId ? { ...n, status: 'read' } : n));
    } catch (e) { console.error(e); }
  };

  const markActioned = async (notifId) => {
    try {
      await api.patch(`/crm/stock-notifications/${notifId}/actioned`);
      setNotifications(prev => prev.map(n => n._id === notifId ? { ...n, status: 'actioned' } : n));
    } catch (e) { console.error(e); }
  };

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-gray-50">
      <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  const unreadCount = notifications.filter(n => n.status === 'unread').length;
  const byGroup = parkedLeads?.by_group || {};
  const allLeads = parkedLeads?.leads || [];

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="stock-notifications-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <Package className="w-5 h-5 text-amber-300" /> Stock Notifications
              </h1>
              <p className="text-xs text-emerald-200">
                {isManager ? `${allLeads.length} leads waiting for stock` : `${unreadCount} unread notifications`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isManager && selectedLeadIds.length > 0 && (
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white h-8 text-xs" onClick={() => setShowNotifyDialog(true)} data-testid="notify-btn">
                <Send className="w-3.5 h-3.5 mr-1" /> Notify ({selectedLeadIds.length})
              </Button>
            )}
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      {isManager && (
        <div className="flex bg-white border-b">
          <button
            onClick={() => setTab('waiting-stock')}
            className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${
              tab === 'waiting-stock' ? 'bg-amber-50 text-amber-700 border-b-2 border-amber-500' : 'text-gray-500 hover:bg-gray-50'
            }`}
            data-testid="tab-waiting-stock"
          >
            <Package className="w-3.5 h-3.5 inline mr-1" /> Waiting for Stock ({allLeads.length})
          </button>
          <button
            onClick={() => setTab('sent-notifications')}
            className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${
              tab === 'sent-notifications' ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500' : 'text-gray-500 hover:bg-gray-50'
            }`}
            data-testid="tab-sent-notifications"
          >
            <Bell className="w-3.5 h-3.5 inline mr-1" /> Sent Notifications ({notifications.length})
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {/* Manager: Waiting for Stock Tab */}
        {(isManager && tab === 'waiting-stock') && (
          <div className="px-4 pt-3 pb-4 space-y-3" data-testid="waiting-stock-list">
            {allLeads.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <p className="font-semibold text-gray-700">No leads waiting for stock</p>
                <p className="text-sm text-gray-400 mt-1">All parked leads have other reasons</p>
              </div>
            ) : (
              Object.entries(byGroup).map(([group, leads]) => {
                const isExpanded = expandedGroup === group || expandedGroup === null;
                const groupSelected = leads.every(l => selectedLeadIds.includes(l._id));
                return (
                  <div key={group} className="bg-white rounded-lg border overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={groupSelected}
                          onChange={() => selectAllInGroup(leads)}
                          className="w-4 h-4 text-amber-600 rounded"
                          data-testid={`select-group-${group}`}
                        />
                        <button onClick={() => setExpandedGroup(isExpanded && expandedGroup !== null ? null : group)} className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-gray-800">{group}</span>
                          <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full font-bold">{leads.length}</span>
                          {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                        </button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="divide-y">
                        {leads.map(lead => (
                          <div key={lead._id} className={`px-4 py-3 flex items-start gap-3 transition-colors ${
                            selectedLeadIds.includes(lead._id) ? 'bg-amber-50/50' : 'hover:bg-gray-50'
                          }`}>
                            <input
                              type="checkbox"
                              checked={selectedLeadIds.includes(lead._id)}
                              onChange={() => toggleLeadSelect(lead._id)}
                              className="w-4 h-4 text-amber-600 rounded mt-0.5"
                              data-testid={`select-lead-${lead._id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm text-gray-900">{lead.product_name}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold border border-amber-200">{lead.reason}</span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
                                <span className="flex items-center gap-1"><User className="w-3 h-3" />{lead.client_name}</span>
                                {lead.client_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.client_phone}</span>}
                                <span>{lead.session_number}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-xs">
                                <span className="text-gray-400">Salesman: {lead.assigned_salesman_name}</span>
                                {lead.follow_up_date && <span className="text-amber-600">Follow-up: {new Date(lead.follow_up_date).toLocaleDateString('en-IN')}</span>}
                              </div>
                            </div>
                            <div className="flex-shrink-0 flex flex-col items-end gap-1">
                              {lead.estimated_value > 0 && (
                                <span className="font-bold text-sm text-emerald-700">₹{Math.round(lead.estimated_value).toLocaleString('en-IN')}</span>
                              )}
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-blue-600 hover:text-blue-800"
                                onClick={() => navigate(`/lead-sessions/${lead.session_id}`)}>
                                <ExternalLink className="w-3 h-3 mr-1" /> View
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Notifications List (Manager: Sent | Salesman: My Notifications) */}
        {((isManager && tab === 'sent-notifications') || isSalesman || (!isManager && !isSalesman)) && (
          <div className="px-4 pt-3 pb-4 space-y-3" data-testid="notifications-list">
            {notifications.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Bell className="w-8 h-8 text-gray-400" />
                </div>
                <p className="font-semibold text-gray-700">No notifications yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  {isManager ? 'Send stock arrival notifications to salesmen' : 'Stock arrival notifications will appear here'}
                </p>
              </div>
            ) : notifications.map(notif => (
              <div key={notif._id} className={`bg-white rounded-lg border overflow-hidden transition-all ${
                notif.status === 'unread' ? 'border-amber-300 shadow-sm' : notif.status === 'actioned' ? 'opacity-60' : ''
              }`} data-testid={`notification-${notif._id}`}>
                <div className="px-4 py-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {notif.status === 'unread' && <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />}
                        <span className="font-medium text-sm text-gray-900">{notif.product_name}</span>
                        {notif.main_group && <span className="text-xs text-gray-500">{notif.main_group}</span>}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                          notif.status === 'unread' ? 'bg-amber-100 text-amber-700' :
                          notif.status === 'read' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }`}>{notif.status}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{notif.message}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-1 flex-wrap">
                        <span>{notif.client_name}</span>
                        {notif.client_phone && <span>{notif.client_phone}</span>}
                        {isSalesman ? (
                          <span>From: {notif.created_by_name}</span>
                        ) : (
                          <span>To: {notif.salesman_name}</span>
                        )}
                        <span>{new Date(notif.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex flex-col gap-1 ml-2">
                      {notif.status === 'unread' && (
                        <Button size="sm" variant="outline" className="h-7 px-2 text-[10px]" onClick={() => markRead(notif._id)} data-testid={`mark-read-${notif._id}`}>
                          Mark Read
                        </Button>
                      )}
                      {notif.status !== 'actioned' && (
                        <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] border-green-300 text-green-600 hover:bg-green-50" onClick={() => markActioned(notif._id)} data-testid={`mark-actioned-${notif._id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Actioned
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] text-blue-600"
                        onClick={() => navigate(`/lead-sessions/${notif.session_id}`)}>
                        <ExternalLink className="w-3 h-3 mr-1" /> Session
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notify Dialog */}
      <Dialog open={showNotifyDialog} onOpenChange={setShowNotifyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Notify Salesmen - Stock Arrived</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Sending notification for <span className="font-bold">{selectedLeadIds.length}</span> lead{selectedLeadIds.length !== 1 ? 's' : ''}.
              The assigned salesman will be notified.
            </p>
            <div>
              <label className="text-xs font-medium text-gray-700">Message</label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                rows={3}
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                placeholder="Enter notification message..."
                data-testid="notify-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotifyDialog(false)}>Cancel</Button>
            <Button className="bg-amber-500 hover:bg-amber-600" onClick={handleNotify} disabled={sending} data-testid="send-notify-btn">
              {sending ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Send Notification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockNotifications;
